import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../utils/seo';

// ===== 専門資格ページ 新デザイン全面最新化（型正典: src/pages/qualifications/business-manner/page.tsx） =====
// 設計note（共通骨格・9ページ共通の設計言語）:
//   紺#1a2942 / 真鍮金#a38e65 / brass2 #8f7a56 / gold-signboard #d7b779 / cream #f8f6f0 / line #e2ddd2。
//   見出し=jba-display（Shippori Mincho B1）+ 金差し色span。eyebrow=Playfair uppercase。BTN-08 2状態。
//   共通セクション骨格（全異型・装飾ディバイダー/素hr/❦/紋章 不使用）:
//     Hero(全幅ビルボード) → I.資格概要(リード3段落+細罫データ行+特徴3枚) → II.カリキュラム(番号2カラム編集ブロック)
//     → III.こんな方におすすめ(紺パネル+2カラム点検リスト) → IV.教材(番号カード) → V.認定試験(紺罫定義リスト)
//     → VI.料金とお申込み(大字serif受講料+含まれるもの) → VII.FAQ(アコーディオン) → VIII.関連資格+締めCTA。
//   実文出典（創作禁止）: 本ページ旧実装の逐語（qual-content.md §5 event-planning）。
//     数値・受講料・章立て・FAQは旧実装から改変せず継承。
// event-planning = Standard（正典と同一骨格） / hero型A(左寄せ全幅) / 紺パネル主役=III.

export default function EventPlanningButlerPage() {
  useEffect(() => {
    updateSEO({
      title: 'Event Planning Butler資格 | イベント企画運営のプロ | 全日本執事協会',
      description: '全日本執事協会認定のEvent Planning Butler資格。パーティー企画、セレモニー進行、ゲスト対応、会場設営など、特別な場を演出するための総合的なスキルを証明する専門資格。執事のホスピタリティで上質なイベントを実現。料金58,000円。',
      keywords: 'Event Planning Butler,イベント企画,パーティー運営,セレモニー進行,執事,全日本執事協会',
      canonical: 'https://butler-association.jp/qualifications/event-planning'
    });
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '資格制度', href: '/qualifications' },
    { label: 'Event Planning' }
  ];

  // BTN-08（canon と同一文法・2状態）
  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const features = [
    { icon: 'ri-lightbulb-line', title: '企画設計力', desc: 'コンセプトから実現までの全体設計。目的とテーマを定め、予算とスケジュールに落とし込む力を養います。' },
    { icon: 'ri-palette-line', title: '空間演出力', desc: '装飾・照明・音楽による雰囲気作り。フラワーアレンジメントから音響まで、記憶に残る空間を作り上げます。' },
    { icon: 'ri-settings-line', title: '運営管理力', desc: '当日進行とトラブル対応の実践力。受付から進行、緊急時の対処まで、催事を滞りなく運営します。' },
  ];

  const curriculum = [
    { no: '01', title: '執事とイベント企画', desc: 'イベントプランナーとしての執事の役割、貴族の館でのレセプションの歴史、ホスピタリティを活かした演出、執事ならではの視点を学びます。' },
    { no: '02', title: 'イベント企画の基本', desc: '企画立案のプロセス、目的とコンセプトの設定方法、予算管理の基礎、実現可能なスケジュール作成技術を習得します。' },
    { no: '03', title: '会場選定とレイアウト', desc: '会場の選び方と評価基準、人数に応じた適切な設営、動線設計の原則、空間を最大限に活かす演出方法を学びます。' },
    { no: '04', title: 'ホームパーティーの企画', desc: '自宅でのおもてなしの基本、テーマ設定とデコレーション、招待状の作成マナー、当日の進行管理とホストのサポートを習得します。' },
    { no: '05', title: 'フォーマルイベントの運営', desc: 'ディナーパーティーの企画と進行、カクテルパーティーの運営、記念式典の設計、セレモニー進行の技術を学びます。' },
    { no: '06', title: 'ゲスト対応とホスピタリティ', desc: '招待客リストの管理方法、受付と案内の実践、席次の配慮と調整、お見送りまでの一貫した流れを習得します。' },
    { no: '07', title: 'ケータリングと飲食管理', desc: 'メニュー選定の基準、業者との効果的な調整、食材アレルギーへの対応、飲料の適切な準備と管理を学びます。' },
    { no: '08', title: '装飾と演出', desc: 'フラワーアレンジメントの基本、テーブルデコレーションの技術、照明と音響の効果的な活用、季節感を活かした演出方法を習得します。' },
    { no: '09', title: 'トラブル対応とリスク管理', desc: '想定されるトラブルと予防策、天候不良時の代替プラン、緊急時の連絡体制構築、保険の基礎知識を学びます。' },
    { no: '10', title: '総合演習', desc: '実際のイベント企画書作成演習、シミュレーション実践、確認テストで学習内容を統合します。' },
  ];

  const recommend = [
    'イベントプランナーを目指す方', 'ホテル・ブライダル業界で働く方', '企業の広報・イベント担当者',
    'ホームパーティーを上質に演出したい方', 'ケータリング業界でスキルアップしたい方', '会場運営・施設管理に携わる方',
    'フリーランスで活動したい方', '執事資格と組み合わせて専門性を高めたい方',
  ];

  const materials = [
    { no: '1', title: 'テキスト教材（約100ページ）', desc: 'イベント企画運営の専門知識を詳しく解説した専用テキスト。' },
    { no: '2', title: 'オンライン動画レッスン', desc: '会場設営や装飾技術を含む実践的な動画解説。視聴期間1年間。' },
    { no: '3', title: 'イベント企画シート集', desc: '実務で使えるチェックリストやテンプレート集。' },
    { no: '4', title: '認定試験問題・解答用紙', desc: '筆記試験とイベント企画書作成課題を含む試験問題。' },
  ];

  const exam = [
    { label: '試験形式', value: '筆記試験（記述式・選択式）+ 企画書作成課題' },
    { label: '合格基準', value: '80点以上' },
    { label: '試験期限', value: '申込から1年以内' },
    { label: '受験資格', value: '特になし（Associate Butler以上推奨）' },
    { label: '受験場所', value: '自宅（通信試験）' },
    { label: '再試験', value: '再試験料10,000円で何度でも可' },
  ];

  const included = [
    'テキスト教材（約100ページ）', 'オンライン動画レッスン（1年間視聴可能）', 'イベント企画シート集',
    '認定試験問題・解答用紙', '試験添削サービス', '合格時の認定証・認定カード', '送料',
  ];

  const related = [
    { href: '/qualifications/dining-coordinator', name: 'Dining Coordinator', ja: '食事とテーブルコーディネートの専門家', icon: 'ri-restaurant-line' },
    { href: '/qualifications/wine-steward', name: 'Wine Steward', ja: 'ワインと酒類の専門家', icon: 'ri-goblet-line' },
    { href: '/qualifications', name: '専門資格一覧を見る', ja: 'すべての専門資格をご覧いただけます', icon: 'ri-list-check' },
  ];

  const faqs = [
    { q: 'イベント業界未経験でも取得できますか？', a: 'はい、基礎から学べる内容です。ホームパーティーを開きたい方にも適しています。' },
    { q: 'ブライダルプランナーとの違いは？', a: 'ブライダルに特化せず、様々なイベント全般の企画運営を執事の視点から学びます。' },
    { q: '企画書作成課題とは何ですか？', a: '与えられた条件に基づいて実際のイベント企画書を作成し提出する実技課題です。' },
    { q: 'フリーランスで活動できますか？', a: '本資格は就職を保証するものではありませんが、独立を目指す方の知識習得に役立つ内容です。' },
    { q: 'ホームパーティーの企画を学べますか？', a: 'はい、第4章で自宅でのパーティー企画を詳しく学びます。' },
    { q: '会場装飾の技術も学べますか？', a: 'はい、第8章でフラワーアレンジメントやテーブルデコレーションを学びます。' },
    { q: '企業イベントにも対応していますか？', a: 'はい、ビジネスレセプションや社内イベントの企画運営も学習内容に含まれます。' },
    { q: '予算管理について学べますか？', a: 'はい、第2章で予算管理の基礎を実践的に学びます。' },
    { q: 'トラブル対応も学べますか？', a: 'はい、第9章で想定されるトラブルと対応方法を詳しく学びます。' },
    { q: '音響や照明についても学べますか？', a: 'はい、第8章で照明と音響の効果的な活用方法を学びます。' },
    { q: 'ケータリング業者との調整方法は？', a: 'はい、第7章で業者選定から調整まで実践的に学びます。' },
    { q: 'ゲストの席次について学べますか？', a: 'はい、第6章でゲスト対応と席次の配慮について詳しく学びます。' },
    { q: '小規模なイベントにも使えますか？', a: 'はい、10名程度の小規模から100名以上の大規模まで対応できる内容です。' },
    { q: 'オンラインイベントにも応用できますか？', a: '基本的なイベント企画の原則を学ぶため、オンラインでも応用可能です。' },
  ];

  return (
    <Layout>
      {/* ===== Hero — 全幅ビルボード型A（画像+紺グラデ+左寄せ）。由来: about/overview ヒーロー ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[540px] lg:min-h-[620px] flex items-center">
        <img
          src="/images/generated/jba-p-021-qual-event.webp"
          alt="イベント企画運営の様子。執事が会場設営とセレモニー進行で特別な催事を演出する場面"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.93)_0%,rgba(16,20,26,0.74)_46%,rgba(16,20,26,0.34)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.3em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Specialist Certification</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(30px,4vw,50px)] leading-[1.3] tracking-[0.04em] mb-3">Event Planning Butler</h1>
            <p className="font-['Noto_Serif_JP',serif] text-[#d7b779] text-base sm:text-lg tracking-[0.12em] mb-6">イベント・プランニング・バトラー</p>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95] max-w-[600px]">
              特別な催事を演出する企画運営のプロフェッショナル。パーティー企画から会場設営、セレモニー進行まで、特別な場を演出するための総合的なスキルを証明します。
            </p>
          </div>
        </div>
      </section>

      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 lg:px-10 py-14 sm:py-20 space-y-16 sm:space-y-24">

          {/* ===== I. 資格概要（リード3段落+細罫データ行+特徴3枚）。由来: about/overview S1 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">I. About This Certification</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              執事のもてなしで、<span className="text-[#a38e65]">記憶に残る催事</span>を
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
              <p>
                Event Planning Butler資格は、各種イベントの企画から運営までを担当できる専門家としての資格です。パーティー企画、セレモニー進行、ゲスト対応、会場設営など、特別な場を演出するための総合的なスキルを証明します。
              </p>
              <p>
                プライベートパーティーから企業レセプション、式典まで、様々な催事を洗練された空間に仕上げる能力を習得。執事のホスピタリティとイベント運営の専門知識を融合させ、記憶に残る上質なイベントを創出できる力を身につけます。
              </p>
              <p>
                ホテル・ブライダル業界や企業の広報・イベント担当、ケータリングや会場運営、フリーランスでの独立など、催事に携わる幅広い場面で役立つ実践的な専門資格です。
              </p>
            </div>

            {/* 細罫データ行 */}
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">受講料（税込）</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">58,000<span className="text-sm text-[#8f7a56] ml-1">円</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">学習期間目安</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">2–4<span className="text-sm text-[#8f7a56] ml-1">ヶ月</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">カリキュラム</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">全10<span className="text-sm text-[#8f7a56] ml-1">章</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">受験・資格</dt>
                <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">自宅受験・永久</dd>
              </div>
            </dl>

            {/* 特徴3枚（アイコン編集カード） */}
            <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 mt-12">
              {features.map((f) => (
                <div key={f.title} className="border-t-2 border-[#a38e65] bg-[#f8f6f0] px-6 py-7">
                  <i className={`${f.icon} text-3xl text-[#a38e65]`}></i>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mt-4 mb-2.5">{f.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-start gap-3 border border-[#a38e65]/40 bg-[#f8f6f0] px-5 py-4">
              <i className="ri-information-line text-[#a38e65] text-xl mt-0.5"></i>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.85]">
                本資格は知識習得を目的とした認定制度であり、就職や収入を保証するものではありません。全日本執事協会が認定する民間資格です。
              </p>
            </div>
          </section>

          {/* ===== II. カリキュラム（番号2カラム編集ブロック+画像）。由来: about/overview S4 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">II. Curriculum</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              学ぶ内容 — <span className="text-[#a38e65]">全10章</span>
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-8">
              イベント企画運営の専門知識を、10の分野で体系的に学びます。企画立案から会場設営、ゲスト対応、装飾・演出、トラブル対応まで、知識だけでなく実践技術も習得できます。
            </p>
            <img
              src="/images/generated/jba-fs-162-qualifications_event-planning-v5.webp"
              alt="イベント企画運営の学習カリキュラム教材。企画立案や会場設営・装飾を解説するテキストと資料"
              className="w-full aspect-[21/9] h-auto object-cover object-center border border-[#a38e65]/30 mb-10"
            />
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
              {curriculum.map((c) => (
                <div key={c.no} className="flex gap-5 border-t border-[#e2ddd2] pt-6">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none shrink-0 pt-1">{c.no}</span>
                  <div>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2 leading-[1.5]">{c.title}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ===== III. こんな方におすすめ（紺パネル+2カラム点検リスト）。由来: about/overview S3 の紺パネル翻案 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">III. Who It's For</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              こんな方におすすめです
            </h2>
            <div className="bg-[#1a2942] px-6 sm:px-10 lg:px-14 py-10 sm:py-12">
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
                {recommend.map((r) => (
                  <div key={r} className="flex items-start gap-3 border-b border-white/10 pb-4">
                    <i className="ri-check-line text-[#d7b779] text-xl mt-0.5 shrink-0"></i>
                    <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#ded8cc] leading-[1.8]">{r}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== IV. お届けする教材（番号カード+画像）。由来: about/overview S2 の罫線カード文法 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">IV. Course Materials</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              お届けする教材
            </h2>
            <div className="grid lg:grid-cols-[1fr_360px] gap-8 lg:gap-12 items-start">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                {materials.map((m) => (
                  <div key={m.no} className="flex gap-4 border-t border-[#e2ddd2] pt-5">
                    <span className="font-['Playfair_Display',serif] text-[#a38e65] text-xl w-8 h-8 shrink-0 flex items-center justify-center border border-[#a38e65]/40 rounded-full">{m.no}</span>
                    <div>
                      <h3 className="jba-display font-medium text-[#1a2942] text-base mb-1.5 leading-[1.5]">{m.title}</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <img
                src="/images/generated/jba-fs-163-qualifications_event-planning-v5.webp"
                alt="イベント企画運営の教材一式。テキスト・動画レッスン・イベント企画シート集がそろった学習セット"
                className="w-full h-56 lg:h-[300px] object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* ===== V. 認定試験について（紺罫定義リスト）。由来: about/overview S2 の定義リスト文法 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">V. Examination</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              認定試験について
            </h2>
            <dl className="border-t-2 border-[#1a2942] max-w-3xl">
              {exam.map((row) => (
                <div key={row.label} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                  <dt className="sm:w-[140px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{row.label}</dt>
                  <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{row.value}</dd>
                </div>
              ))}
            </dl>
            <p className="font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.9] mt-5 max-w-3xl">
              試験は自宅で行う通信試験です。教材が届いたら、ご自身のペースで学習を進め、準備ができた段階で試験問題に取り組みます。解答用紙を返送いただくと、当協会が添削・採点し、80点以上で合格。認定証と認定カードを発行します。
            </p>
          </section>

          {/* ===== VI. 料金とお申込み（大字serif受講料+含まれるもの）。受講料は22–26px級以上serif強調 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">VI. Enrollment</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              料金とお申込み
            </h2>
            <div className="bg-[#f8f6f0] border border-[#a38e65]/30 px-6 sm:px-10 lg:px-12 py-10">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 border-b border-[#e2ddd2] pb-8 mb-8">
                <div>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.08em] mb-2">受講料（税込・送料込）</p>
                  <div className="jba-display text-[#1a2942] leading-none">
                    <span className="font-['Playfair_Display',serif] text-[clamp(44px,7vw,64px)] font-semibold">58,000</span>
                    <span className="text-2xl sm:text-3xl ml-1">円</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-6 text-center sm:text-right">
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">学習期間目安</p>
                    <p className="jba-display text-[#1a2942] text-base">2〜4ヶ月</p>
                  </div>
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">試験期限</p>
                    <p className="jba-display text-[#1a2942] text-base">1年以内</p>
                  </div>
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">資格有効期限</p>
                    <p className="jba-display text-[#1a2942] text-base">永久</p>
                  </div>
                </div>
              </div>
              <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-5">料金に含まれるもの</h3>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {included.map((it) => (
                  <div key={it} className="flex items-start gap-3">
                    <i className="ri-check-line text-[#a38e65] text-lg mt-0.5 shrink-0"></i>
                    <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.8]">{it}</span>
                  </div>
                ))}
              </div>
              <div className="mt-9">
                <a href="/contact" className={`${BTN08_ON_LIGHT} w-full sm:w-auto`}>Event Planning Butler資格に申し込む</a>
              </div>
            </div>
            <ul className="mt-6 space-y-2">
              {['本資格は全日本執事協会が認定する民間資格です', '資格取得により就職・転職・収入を保証するものではありません', '知識習得と自己研鑽を目的としています'].map((n) => (
                <li key={n} className="flex items-start gap-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.85]">
                  <i className="ri-information-line text-[#a38e65] text-base mt-0.5 shrink-0"></i>
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* ===== VII. よくある質問（アコーディオン） ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">VII. FAQ</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくある質問
            </h2>
            <div className="border-t border-[#e2ddd2]">
              {faqs.map((f) => (
                <details key={f.q} className="group border-b border-[#e2ddd2]">
                  <summary className="flex items-center justify-between gap-4 cursor-pointer py-5 list-none">
                    <span className="jba-display font-medium text-[#1a2942] text-base sm:text-[17px] leading-[1.6]">{f.q}</span>
                    <i className="ri-add-line text-[#a38e65] text-xl shrink-0 transition-transform group-open:rotate-45"></i>
                  </summary>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95] pb-5 pr-8">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* ===== VIII. 関連資格 + 締めCTA（紺パネル）。由来: about/overview S6 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">VIII. Related Certifications</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              あわせて学びたい専門資格
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-8">
              他の専門資格も組み合わせて取得することで、より幅広い専門性を証明できます。
            </p>
            <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 mb-12">
              {related.map((r) => (
                <a key={r.href} href={r.href} className="group block border border-[#e2ddd2] hover:border-[#a38e65] bg-white px-6 py-7 transition-colors">
                  <i className={`${r.icon} text-2xl text-[#a38e65]`}></i>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mt-4 mb-1.5 leading-[1.5]">{r.name}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85] mb-4">{r.ja}</p>
                  <span className="inline-flex items-center gap-2 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] group-hover:text-[#a38e65] transition-colors">
                    詳しく見る<i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                  </span>
                </a>
              ))}
            </div>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                Event Planning Butler資格について、ご不明な点やお申込みのご相談はお気軽にお問い合わせください。専門資格の全体像は資格制度の一覧からもご覧いただけます。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせ・申込</a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>資格制度の一覧を見る</a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
